import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../common/Card';
import { Badge } from '../common/Badge';
import { prosthesisApi } from '../../services/api';
import {
  SuiviLivraison,
  CreateSuiviLivraisonData,
  CommandeProthese
} from '../../types';
import {
  Clock,
  Plus,
  Package,
  Truck,
  CheckCircle,
  Calendar,
  FileText,
  User,
  MapPin
} from 'lucide-react';

interface ProsthesisTimelineProps {
  commandeId?: number;
  showAddButton?: boolean;
  onAddSuivi?: (data: CreateSuiviLivraisonData) => void;
}

const ProsthesisTimeline: React.FC<ProsthesisTimelineProps> = ({
  commandeId,
  showAddButton = false,
  onAddSuivi
}) => {
  const [suivis, setSuivis] = useState<SuiviLivraison[]>([]);
  const [commande, setCommande] = useState<CommandeProthese | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSuivi, setNewSuivi] = useState<CreateSuiviLivraisonData>({
    etape: '',
    commentaire: '',
    transporteur: '',
    numero_suivi: ''
  });

  const etapeOptions = [
    { value: 'empreintes_envoyees', label: 'Empreintes envoyées' },
    { value: 'empreintes_recues_labo', label: 'Empreintes reçues par le labo' },
    { value: 'fabrication_commencee', label: 'Fabrication commencée' },
    { value: 'fabrication_terminee', label: 'Fabrication terminée' },
    { value: 'prothese_expedie', label: 'Prothèse expédiée' },
    { value: 'prothese_en_transit', label: 'Prothèse en transit' },
    { value: 'prothese_livree', label: 'Prothèse livrée au cabinet' },
    { value: 'prothese_posee', label: 'Prothèse posée chez le patient' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (commandeId) {
          // Charger les détails de la commande et ses suivis
          const [commandeResponse] = await Promise.all([
            prosthesisApi.getCommande(commandeId)
          ]);

          setCommande(commandeResponse.data);
          setSuivis(commandeResponse.data.suivis || []);
        } else {
          // Charger la timeline générale
          const response = await prosthesisApi.getTimelineSuivis();
          setSuivis(response.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [commandeId]);

  const getEtapeIcon = (etape: string) => {
    switch (etape) {
      case 'empreintes_envoyees':
      case 'empreintes_recues_labo':
        return <Package className="h-4 w-4" />;
      case 'fabrication_commencee':
      case 'fabrication_terminee':
        return <Clock className="h-4 w-4" />;
      case 'prothese_expedie':
      case 'prothese_en_transit':
        return <Truck className="h-4 w-4" />;
      case 'prothese_livree':
      case 'prothese_posee':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getEtapeColor = (etape: string) => {
    switch (etape) {
      case 'empreintes_envoyees':
      case 'empreintes_recues_labo':
        return 'bg-blue-500';
      case 'fabrication_commencee':
        return 'bg-yellow-500';
      case 'fabrication_terminee':
        return 'bg-orange-500';
      case 'prothese_expedie':
      case 'prothese_en_transit':
        return 'bg-purple-500';
      case 'prothese_livree':
      case 'prothese_posee':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const handleSubmitSuivi = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (commandeId) {
        const response = await prosthesisApi.ajouterSuivi(commandeId, newSuivi);
        // Recharger les données
        const commandeResponse = await prosthesisApi.getCommande(commandeId);
        setCommande(commandeResponse.data);
        setSuivis(commandeResponse.data.suivis || []);
      } else if (onAddSuivi) {
        onAddSuivi(newSuivi);
      }

      setShowAddForm(false);
      setNewSuivi({
        etape: '',
        commentaire: '',
        transporteur: '',
        numero_suivi: ''
      });
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout du suivi:', error);
      // Afficher la cause à l'utilisateur au lieu d'échouer silencieusement
      const data = error?.response?.data;
      const detail = data && typeof data === 'object'
        ? Object.entries(data)
          .map(([champ, msgs]) => `${champ} : ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('\n')
        : "Erreur lors de l'ajout du suivi. Veuillez réessayer.";
      alert(detail);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {commandeId ? `Suivi de la commande ${commande?.numero_commande}` : 'Timeline des suivis'}
        </h2>
        {showAddButton && (
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Ajouter un suivi
          </button>
        )}
      </div>

      {commandeId && commande && (
        <Card>
          <CardHeader>
            <CardTitle>Informations de la commande</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Patient</p>
                <p className="font-medium text-gray-600">{commande.patient_info?.nom} {commande.patient_info?.prenoms}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Prestataire</p>
                <p className="font-medium text-gray-600">{commande.prestataire_info?.nom}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Type de prothèse</p>
                <p className="font-medium text-gray-600">{commande.type_prothese_info?.nom}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Statut actuel</p>
                <Badge variant="primary">{commande.status_display}</Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Date de livraison prévue</p>
                <p className="font-medium text-gray-600">{formatDate(commande.date_livraison_prevue)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Coût total</p>
                <p className="font-medium text-gray-600">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'GNF' }).format(commande.cout_total)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulaire d'ajout de suivi */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Ajouter une étape de suivi</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitSuivi} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="etape-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Étape *
                  </label>
                  <select
                    id="etape-select"
                    value={newSuivi.etape}
                    onChange={(e) => setNewSuivi({ ...newSuivi, etape: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Sélectionner une étape</option>
                    {etapeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transporteur
                  </label>
                  <input
                    type="text"
                    value={newSuivi.transporteur}
                    onChange={(e) => setNewSuivi({ ...newSuivi, transporteur: e.target.value })}
                    placeholder="Nom du transporteur"
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Numéro de suivi
                  </label>
                  <input
                    type="text"
                    value={newSuivi.numero_suivi}
                    onChange={(e) => setNewSuivi({ ...newSuivi, numero_suivi: e.target.value })}
                    placeholder="Numéro de suivi transporteur"
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire
                </label>
                <textarea
                  value={newSuivi.commentaire}
                  onChange={(e) => setNewSuivi({ ...newSuivi, commentaire: e.target.value })}
                  rows={3}
                  placeholder="Commentaire sur cette étape..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des étapes</CardTitle>
        </CardHeader>
        <CardContent>
          {suivis.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun suivi</h3>
              <p className="mt-1 text-sm text-gray-500">
                Aucune étape de suivi n'a encore été enregistrée.
              </p>
            </div>
          ) : (
            <div className="flow-root">
              <ul className="-mb-8">
                {suivis.map((suivi, index) => (
                  <li key={suivi.id}>
                    <div className="relative pb-8">
                      {index !== suivis.length - 1 && (
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className={`h-8 w-8 rounded-full ${getEtapeColor(suivi.etape)} flex items-center justify-center ring-8 ring-white`}>
                            <div className="text-white">
                              {getEtapeIcon(suivi.etape)}
                            </div>
                          </span>
                        </div>
                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {suivi.etape_display}
                            </p>
                            {suivi.commentaire && (
                              <p className="mt-1 text-sm text-gray-600">
                                {suivi.commentaire}
                              </p>
                            )}
                            <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                              {suivi.transporteur && (
                                <div className="flex items-center gap-1">
                                  <Truck className="h-3 w-3" />
                                  {suivi.transporteur}
                                </div>
                              )}
                              {suivi.numero_suivi && (
                                <div className="flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  {suivi.numero_suivi}
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {suivi.created_by_name}
                              </div>
                            </div>
                          </div>
                          <div className="whitespace-nowrap text-right text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(suivi.date_etape)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProsthesisTimeline;